<template>
  <div class="flex flex-col gap-2 w-1/2">
    <div class="flex flex-col gap-3">
      <FormKit
        type="toggle"
        data-testid="share-expiration-views-toggle"
        v-model="isExpiresAfterViews"
        label="After number of views"
        @click="isExpiresAfterViews && debounceViews(null)"
      />
      <div v-if="isExpiresAfterViews" class="number-of-views mb-4">
        <FormKit
          ref="viewsRef"
          data-testid="share-expiration-views-input"
          type="number"
          validation="required|min:1"
          validation-label="number of views"
          validation-visibility="dirty"
          v-model="viewsStr"
          @keyup="onViewsKeyUp"
          @change="debounceViews()"
        />
      </div>
    </div>
    <div class="flex flex-col gap-3">
      <FormKit
        type="toggle"
        data-testid="share-expiration-date-toggle"
        v-model="isExpiresAt"
        label="On a specific date"
        @click="isExpiresAt && debounceDate(null)"
      />
      <FormKit
        v-if="isExpiresAt"
        data-testid="share-expiration-date-input"
        type="date"
        ref="dateRef"
        help="The date you want to block access"
        :min="tomorrow"
        v-model="selectedDate"
        @change="debounceDate()"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import moment from 'moment';
import { computed, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';

import { useFormKit } from '@/composables/useFormKit';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';

const DEBOUNCE_TIME = 500;

const viewerItemStore = useViewerItemStore();
const { expiresAfterViews, expiresAt } = storeToRefs(viewerItemStore);
const { isValidRef } = useFormKit();

const isExpiredAfterViewsEnabled = (value: number | undefined) => typeof value === 'number' && value > 0;
const isExpiredAt = (value: string | undefined) => typeof value === 'string' && value !== '';
const formatExpiresAt = (value: string | undefined) => moment(value).format('YYYY-MM-DD');

const isExpiresAfterViews = ref(isExpiredAfterViewsEnabled(expiresAfterViews.value));
const views = ref(expiresAfterViews.value);
const isExpiresAt = ref(isExpiredAt(expiresAt.value));
const selectedDate = ref(formatExpiresAt(expiresAt.value));

const viewsRef = ref(null);
const dateRef = ref(null);

const viewsStr = computed({
  get: () => views.value?.toString() ?? '0',
  set: (value: string) => {
    const number = Number(value);
    if (!isNaN(number)) {
      views.value = number;
    }
  },
});

const tomorrow = computed(() => moment().add(1, 'day').format('YYYY-MM-DD'));

const updateViews = (overrideValue?: number | null) => {
  // override skips field validation check (null disables feature)
  if (overrideValue === undefined && !isValidRef(viewsRef)) {
    return;
  }

  const value = overrideValue === undefined ? views.value : overrideValue;
  if (value != expiresAfterViews.value) {
    viewerItemStore.saveExpiration({ expiresAfterViews: value });
  }
};

const updateDate = (overrideDate?: string | null) => {
  // override skips field validation check (empty string disables feature)
  if (overrideDate === undefined && !isValidRef(dateRef)) {
    return;
  }

  // @todo this validation should be handled in validator (after formkit pro integration)
  const newDate = moment(selectedDate.value);
  const expiresAtValue = overrideDate === undefined ? newDate.toISOString() : overrideDate;
  if (expiresAtValue === null || (newDate.isValid() && newDate.isSameOrAfter(tomorrow.value))) {
    viewerItemStore.saveExpiration({ expiresAt: expiresAtValue });
  }
};

const debounceViews = useDebounceFn(updateViews, DEBOUNCE_TIME);
const debounceDate = useDebounceFn(updateDate, DEBOUNCE_TIME);

const onViewsKeyUp = (event: KeyboardEvent) => {
  if (isNaN(Number(event.key)) && event.key !== 'Backspace') {
    return;
  }

  debounceViews();
};

watch(expiresAfterViews, value => {
  views.value = value;
  isExpiresAfterViews.value = isExpiredAfterViewsEnabled(value);
});

watch(expiresAt, value => {
  isExpiresAt.value = isExpiredAt(value);
  selectedDate.value = formatExpiresAt(value);
});
</script>

<style scoped>
.number-of-views :deep(.formkit-wrapper) {
  width: 100px !important;
}
</style>
